import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Search,
  Loader2,
  ChevronLeft,
  ChevronRight,
  Edit,
  X,
  User,
  Gift,
  History,
  Pencil,
} from 'lucide-react';
import { apiRequest } from '../lib/api';

interface UserData {
  id: string;
  wallet_address: string;
  created_at: string;
  profiles: {
    username: string | null;
    display_name: string | null;
  } | null;
  trading_stats: {
    total_pnl: number;
    total_volume: number;
  } | null;
  user_fee_overrides: {
    custom_trading_fee: number | null;
    custom_referral_l1: number | null;
    custom_referral_l2: number | null;
    fee_tier_id: string | null;
  } | null;
  referral_count: number;
}

interface UsernameHistoryEntry {
  old_username: string;
  new_username: string;
  changed_at: string;
  changed_by: string;
  change_type: string;
}

interface EditModalProps {
  user: UserData;
  onClose: () => void;
  onSave: (data: any) => void;
  isLoading: boolean;
}

// Username History Modal
function UsernameHistoryModal({ user, onClose }: { user: UserData; onClose: () => void }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['username-history', user.wallet_address],
    queryFn: () => apiRequest('GET', `/api/admin/users/${user.wallet_address}/username-history`),
  });

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-zinc-900 border border-white/10 w-full max-w-md">
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <h2 className="text-sm font-medium text-white">Username History</h2>
          <button onClick={onClose} className="text-zinc-400 hover:text-white">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-4">
          {/* User Info */}
          <div className="p-3 bg-white/5 border border-white/10 mb-4">
            <p className="text-xs text-zinc-500 mb-1">Current Username</p>
            <p className="text-sm font-mono text-white">
              @{user.profiles?.username || 'N/A'}
            </p>
          </div>

          {/* History List */}
          {isLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-5 h-5 animate-spin text-zinc-600" />
            </div>
          ) : error ? (
            <p className="text-center text-red-400 text-sm py-4">Failed to load history</p>
          ) : data?.history?.length === 0 ? (
            <p className="text-center text-zinc-500 text-sm py-4">No username changes</p>
          ) : (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {data?.history?.map((entry: UsernameHistoryEntry, i: number) => (
                <div key={i} className="p-3 bg-black/50 border border-white/5">
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-zinc-500">@{entry.old_username}</span>
                    <span className="text-zinc-600">→</span>
                    <span className="text-white">@{entry.new_username}</span>
                    {entry.change_type === 'admin_force' && (
                      <span className="px-1.5 py-0.5 text-[10px] bg-red-500/20 text-red-400">
                        ADMIN
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-zinc-600 mt-1">
                    {new Date(entry.changed_at).toLocaleString()}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="p-4 border-t border-white/10">
          <button
            onClick={onClose}
            className="w-full px-4 py-2 text-sm text-zinc-400 border border-white/10 hover:bg-white/5"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

// Force Username Change Modal
function ForceUsernameChangeModal({
  user,
  onClose,
}: {
  user: UserData;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [newUsername, setNewUsername] = useState('');
  const [reason, setReason] = useState('');

  const mutation = useMutation({
    mutationFn: () =>
      apiRequest('PUT', `/api/admin/users/${user.wallet_address}/username`, {
        username: newUsername,
        reason,
      }),
    onSuccess: () => {
      toast.success('Username changed successfully');
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      onClose();
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to change username');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newUsername.trim()) {
      toast.error('Username is required');
      return;
    }
    mutation.mutate();
  };

  // Basic validation
  const isValidFormat =
    newUsername.length >= 4 &&
    newUsername.length <= 15 &&
    /^[a-zA-Z0-9_]+$/.test(newUsername) &&
    !/__/.test(newUsername);

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-zinc-900 border border-white/10 w-full max-w-md">
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <h2 className="text-sm font-medium text-white">Force Change Username</h2>
          <button onClick={onClose} className="text-zinc-400 hover:text-white">
            <X className="w-4 h-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* Current username */}
          <div className="p-3 bg-white/5 border border-white/10">
            <p className="text-xs text-zinc-500 mb-1">Current Username</p>
            <p className="text-sm font-mono text-white">
              @{user.profiles?.username || 'N/A'}
            </p>
          </div>

          {/* New username */}
          <div>
            <label className="block text-xs text-zinc-500 mb-1">New Username</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500">@</span>
              <input
                type="text"
                value={newUsername}
                onChange={(e) => setNewUsername(e.target.value)}
                placeholder="new_username"
                maxLength={15}
                className="w-full pl-7 pr-4 py-2 text-sm bg-black border border-white/10 text-white
                  placeholder:text-zinc-600 focus:outline-none focus:border-emerald-500"
              />
            </div>
            {newUsername && !isValidFormat && (
              <p className="text-xs text-red-400 mt-1">
                4-15 chars, letters/numbers/underscores only, no consecutive underscores
              </p>
            )}
          </div>

          {/* Reason */}
          <div>
            <label className="block text-xs text-zinc-500 mb-1">Reason for change</label>
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Policy violation, impersonation, etc."
              className="w-full px-3 py-2 text-sm bg-black border border-white/10 text-white
                placeholder:text-zinc-600 focus:outline-none focus:border-emerald-500"
            />
          </div>

          {/* Warning */}
          <div className="p-3 bg-amber-500/10 border border-amber-500/20 text-xs text-amber-400">
            This action will permanently retire the old username. The user will be notified.
          </div>

          {/* Actions */}
          <div className="flex gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 text-sm text-zinc-400 border border-white/10 hover:bg-white/5"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={mutation.isPending || !isValidFormat}
              className="flex-1 px-4 py-2 text-sm bg-red-600 hover:bg-red-700 text-white
                disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {mutation.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                'Force Change'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function EditIncentivesModal({ user, onClose, onSave, isLoading }: EditModalProps) {
  const [formData, setFormData] = useState({
    custom_trading_fee: user.user_fee_overrides?.custom_trading_fee?.toString() || '',
    custom_referral_l1: user.user_fee_overrides?.custom_referral_l1?.toString() || '',
    custom_referral_l2: user.user_fee_overrides?.custom_referral_l2?.toString() || '',
    override_reason: '',
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({
      custom_trading_fee: formData.custom_trading_fee ? parseFloat(formData.custom_trading_fee) : null,
      custom_referral_l1: formData.custom_referral_l1 ? parseFloat(formData.custom_referral_l1) : null,
      custom_referral_l2: formData.custom_referral_l2 ? parseFloat(formData.custom_referral_l2) : null,
      override_reason: formData.override_reason || 'Manual override',
    });
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-zinc-900 border border-white/10 w-full max-w-md">
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <h2 className="text-sm font-medium text-white">Edit Incentives</h2>
          <button onClick={onClose} className="text-zinc-400 hover:text-white">
            <X className="w-4 h-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* User Info */}
          <div className="p-3 bg-white/5 border border-white/10">
            <p className="text-xs text-zinc-500 mb-1">User</p>
            <p className="text-sm font-mono text-white">
              {user.profiles?.username || user.wallet_address.slice(0, 10) + '...'}
            </p>
          </div>

          {/* Custom Trading Fee */}
          <div>
            <label className="block text-xs text-zinc-500 mb-1">
              Custom Trading Fee (e.g., 0.001 = 0.1%)
            </label>
            <input
              type="number"
              step="0.00001"
              min="0"
              max="1"
              value={formData.custom_trading_fee}
              onChange={(e) => setFormData({ ...formData, custom_trading_fee: e.target.value })}
              placeholder="Leave empty for default"
              className="w-full px-3 py-2 text-sm bg-black border border-white/10 text-white
                placeholder:text-zinc-600 focus:outline-none focus:border-emerald-500"
            />
          </div>

          {/* L1 Commission */}
          <div>
            <label className="block text-xs text-zinc-500 mb-1">
              L1 Referral Commission (e.g., 0.05 = 5%)
            </label>
            <input
              type="number"
              step="0.01"
              min="0"
              max="1"
              value={formData.custom_referral_l1}
              onChange={(e) => setFormData({ ...formData, custom_referral_l1: e.target.value })}
              placeholder="Leave empty for default (5%)"
              className="w-full px-3 py-2 text-sm bg-black border border-white/10 text-white
                placeholder:text-zinc-600 focus:outline-none focus:border-emerald-500"
            />
          </div>

          {/* L2 Commission */}
          <div>
            <label className="block text-xs text-zinc-500 mb-1">
              L2 Referral Commission (e.g., 0.02 = 2%)
            </label>
            <input
              type="number"
              step="0.01"
              min="0"
              max="1"
              value={formData.custom_referral_l2}
              onChange={(e) => setFormData({ ...formData, custom_referral_l2: e.target.value })}
              placeholder="Leave empty for default (2%)"
              className="w-full px-3 py-2 text-sm bg-black border border-white/10 text-white
                placeholder:text-zinc-600 focus:outline-none focus:border-emerald-500"
            />
          </div>

          {/* Reason */}
          <div>
            <label className="block text-xs text-zinc-500 mb-1">Reason for override</label>
            <input
              type="text"
              value={formData.override_reason}
              onChange={(e) => setFormData({ ...formData, override_reason: e.target.value })}
              placeholder="VIP partnership, volume incentive, etc."
              className="w-full px-3 py-2 text-sm bg-black border border-white/10 text-white
                placeholder:text-zinc-600 focus:outline-none focus:border-emerald-500"
            />
          </div>

          {/* Actions */}
          <div className="flex gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 text-sm text-zinc-400 border border-white/10 hover:bg-white/5"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="flex-1 px-4 py-2 text-sm bg-emerald-600 hover:bg-emerald-700 text-white
                disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function Users() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [editingUser, setEditingUser] = useState<UserData | null>(null);
  const [historyUser, setHistoryUser] = useState<UserData | null>(null);
  const [usernameChangeUser, setUsernameChangeUser] = useState<UserData | null>(null);

  // Fetch users
  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-users', page, search],
    queryFn: () => {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '20',
        ...(search && { search }),
      });
      return apiRequest('GET', `/api/admin/users?${params}`);
    },
  });

  // Update incentives mutation
  const updateMutation = useMutation({
    mutationFn: ({ userId, data }: { userId: string; data: any }) =>
      apiRequest('PUT', `/api/admin/users/${userId}/incentives`, data),
    onSuccess: () => {
      toast.success('Incentives updated successfully');
      setEditingUser(null);
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to update incentives');
    },
  });

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-medium text-white">Users</h1>
          <p className="text-sm text-zinc-500">Manage user accounts and incentives</p>
        </div>
      </div>

      {/* Search */}
      <form onSubmit={handleSearch} className="mb-6">
        <div className="flex gap-2">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by wallet or username..."
              className="w-full pl-10 pr-4 py-2 text-sm bg-black border border-white/10 text-white
                placeholder:text-zinc-600 focus:outline-none focus:border-emerald-500"
            />
          </div>
          <button
            type="submit"
            className="px-4 py-2 text-sm bg-white/5 border border-white/10 text-zinc-300
              hover:bg-white/10 hover:text-white"
          >
            Search
          </button>
        </div>
      </form>

      {/* Users Table */}
      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-zinc-600" />
        </div>
      ) : error ? (
        <div className="text-center py-12 text-red-400">Failed to load users</div>
      ) : (
        <>
          <div className="border border-white/10 overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="bg-white/5">
                  <th className="text-left text-xs text-zinc-500 px-4 py-3">User</th>
                  <th className="text-left text-xs text-zinc-500 px-4 py-3">Referrals</th>
                  <th className="text-left text-xs text-zinc-500 px-4 py-3">Custom Fees</th>
                  <th className="text-left text-xs text-zinc-500 px-4 py-3">Joined</th>
                  <th className="text-right text-xs text-zinc-500 px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {data?.users?.map((user: UserData) => (
                  <tr key={user.wallet_address} className="border-t border-white/5 hover:bg-white/5">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-white/5 flex items-center justify-center">
                          <User className="w-4 h-4 text-zinc-500" />
                        </div>
                        <div>
                          <p className="text-sm text-white">
                            @{user.profiles?.username || 'unnamed'}
                          </p>
                          <p className="text-xs font-mono text-zinc-500">
                            {user.wallet_address.slice(0, 6)}...{user.wallet_address.slice(-4)}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1 text-sm">
                        <Gift className="w-3 h-3 text-emerald-500" />
                        <span className="text-zinc-300">{user.referral_count || 0}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {user.user_fee_overrides?.custom_trading_fee ? (
                        <span className="text-xs px-2 py-0.5 bg-amber-500/20 text-amber-400">
                          {(user.user_fee_overrides.custom_trading_fee * 100).toFixed(2)}%
                        </span>
                      ) : (
                        <span className="text-xs text-zinc-500">Default</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-zinc-500">
                      {new Date(user.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        {/* Username History */}
                        <button
                          onClick={() => setHistoryUser(user)}
                          title="Username History"
                          className="p-1.5 text-zinc-400 hover:text-white hover:bg-white/10"
                        >
                          <History className="w-4 h-4" />
                        </button>
                        {/* Force Username Change */}
                        <button
                          onClick={() => setUsernameChangeUser(user)}
                          title="Change Username"
                          className="p-1.5 text-zinc-400 hover:text-amber-400 hover:bg-amber-500/10"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        {/* Edit Incentives */}
                        <button
                          onClick={() => setEditingUser(user)}
                          title="Edit Incentives"
                          className="p-1.5 text-zinc-400 hover:text-white hover:bg-white/10"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {data?.pagination && (
            <div className="flex items-center justify-between mt-4">
              <p className="text-xs text-zinc-500">
                Showing {data.users.length} of {data.pagination.total} users
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage(page - 1)}
                  disabled={page === 1}
                  className="p-1.5 text-zinc-400 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <span className="text-xs text-zinc-400">
                  Page {page} of {data.pagination.totalPages}
                </span>
                <button
                  onClick={() => setPage(page + 1)}
                  disabled={page >= data.pagination.totalPages}
                  className="p-1.5 text-zinc-400 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Edit Incentives Modal */}
      {editingUser && (
        <EditIncentivesModal
          user={editingUser}
          onClose={() => setEditingUser(null)}
          onSave={(data) => updateMutation.mutate({ userId: editingUser.wallet_address, data })}
          isLoading={updateMutation.isPending}
        />
      )}

      {/* Username History Modal */}
      {historyUser && (
        <UsernameHistoryModal user={historyUser} onClose={() => setHistoryUser(null)} />
      )}

      {/* Force Username Change Modal */}
      {usernameChangeUser && (
        <ForceUsernameChangeModal
          user={usernameChangeUser}
          onClose={() => setUsernameChangeUser(null)}
        />
      )}
    </div>
  );
}
